/**
 * SME Financing Page — Finance request status tracker
 *
 * Shows:
 * - Active and past finance requests
 * - Status for each (Draft → Funded)
 * - Actions: View details, Download finance pack
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import client from "@/api/client";
import {
  DollarSign, RefreshCw, ChevronDown, AlertCircle, CheckCircle2,
  Clock, Download, Plus, ArrowRight, FileText, Info
} from "lucide-react";
import { toast } from "sonner";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Status config ──────────────────────────────────────────────────────────
const STATUS_META = {
  draft:        { label: "Draft",         color: "bg-slate-100 text-slate-600",   dot: "bg-slate-400"    },
  submitted:    { label: "Submitted",     color: "bg-blue-100 text-blue-700",     dot: "bg-blue-500"     },
  under_review: { label: "Under Review",  color: "bg-amber-100 text-amber-700",   dot: "bg-amber-500"    },
  info_requested:{ label: "Info Needed",  color: "bg-orange-100 text-orange-700", dot: "bg-orange-500 animate-pulse" },
  qualified:    { label: "Qualified",     color: "bg-teal-100 text-teal-700",     dot: "bg-teal-500"     },
  declined:     { label: "Declined",      color: "bg-red-100 text-red-700",       dot: "bg-red-400"      },
  approved:     { label: "Approved",      color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500"  },
  funded:       { label: "Funded",        color: "bg-green-100 text-green-700",   dot: "bg-green-500"    },
};

const REQUEST_LABELS = {
  capex_support:      "Equipment / Capital Support",
  green_loan:         "Green Loan",
  grant:              "Grant / Subsidy",
  carbon_project_dev: "Carbon Project Development",
  blended_finance:    "Blended Finance",
};

// ── Status Badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${meta.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

// ── Request Card ──────────────────────────────────────────────────────────
function RequestCard({ req }) {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pack = req.auto_generated_pack;

  const handleDownloadPack = async () => {
    setDownloading(true);
    try {
      // Download the auto-generated pack as JSON
      const blob = new Blob(
        [JSON.stringify({ finance_request: req.request_id, ...pack }, null, 2)],
        { type: "application/json" }
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `finance-pack-${req.request_id}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Finance pack downloaded");
    } catch {
      toast.error("Download failed");
    }
    setDownloading(false);
  };

  const createdDate = new Date(req.created_at).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric"
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"
      data-testid={`finance-request-${req.request_id}`}>
      {/* Header */}
      <div className="px-6 py-4 flex items-start gap-4">
        <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <DollarSign size={18} className="text-teal-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="text-sm font-semibold text-slate-900 font-mono">{req.request_id}</p>
            <StatusBadge status={req.status} />
          </div>
          <p className="text-xs text-slate-500">
            {REQUEST_LABELS[req.request_type] || req.request_type}
            {req.amount_requested_usd ? ` · $${req.amount_requested_usd.toLocaleString()}` : ""}
            {" · "}Submitted {createdDate}
          </p>
          {req.purpose && <p className="text-xs text-slate-400 mt-0.5 truncate">{req.purpose}</p>}
        </div>
        <button onClick={() => setExpanded(!expanded)}
          className="flex-shrink-0 p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* FI review message if info requested */}
      {req.status === "info_requested" && (
        <div className="mx-6 mb-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <AlertCircle size={14} className="text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-orange-700">Additional information requested</p>
            <p className="text-xs text-orange-600">A reviewer needs more information to proceed. Contact your finance partner.</p>
          </div>
        </div>
      )}

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-slate-100 px-6 py-4 space-y-4">
          {/* Finance pack summary */}
          {pack && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Finance Pack Summary</p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  ["Total Emissions", `${pack.total_tco2e} tCO₂e`, "text-slate-700"],
                  ["CARS Score", Math.round(pack.cars_score), "text-teal-600"],
                  ["Data Quality", pack.confidence?.charAt(0).toUpperCase() + pack.confidence?.slice(1), "text-amber-600"],
                ].map(([l, v, c]) => (
                  <div key={l} className="text-center">
                    <p className={`text-base font-bold ${c}`}>{v}</p>
                    <p className="text-xs text-slate-400">{l}</p>
                  </div>
                ))}
              </div>
              {pack.top_actions?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-400 mb-2">Top opportunities (frozen at submission)</p>
                  {pack.top_actions.slice(0, 2).map((a, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span className="text-slate-600 capitalize">{a.type?.replace(/_/g," ")}</span>
                      <span className="font-semibold text-slate-700">{a.co2_reduction}t CO₂e/yr</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status timeline */}
          {req.status_history?.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Status History</p>
              <div className="space-y-1.5">
                {req.status_history.slice(-3).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                    <span className="text-slate-500 capitalize">{h.status?.replace(/_/g," ")}</span>
                    <span className="text-slate-300 ml-auto">
                      {new Date(h.at).toLocaleDateString("en-GB", { day:"numeric", month:"short" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {pack && (
              <button onClick={handleDownloadPack} disabled={downloading}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl hover:bg-slate-50"
                data-testid={`download-pack-${req.request_id}`}>
                {downloading ? <RefreshCw size={12} className="animate-spin" /> : <Download size={12} />}
                Download Finance Pack
              </button>
            )}
            {(req.status === "approved" || req.status === "funded") && (
              <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle2 size={13} className="text-emerald-600" />
                <span className="text-xs font-semibold text-emerald-700">
                  {req.status === "funded" ? "Funded" : "Approved — awaiting disbursement"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function FinancingPage() {
  const navigate = useNavigate();
  const { journeyData } = useJourney();
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const hasData = !!(journeyData?.footprint?.total_tco2e);

  useEffect(() => {
    client.get("/finance-requests/")
      .then(r => { setRequests(r.data?.requests || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-6 lg:p-8 flex items-center justify-center min-h-[50vh]">
      <RefreshCw size={20} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-7">
          
      <PageHeader
        title="Financing"
        subtitle="Track and manage your financing requests"
      />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Financing Requests" value={requests.length} color="text-slate-900" />
      </div>
        {hasData && (
          <button onClick={() => navigate("/sme-journey")}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700"
            data-testid="new-request-btn">
            <Plus size={14} /> New Request
          </button>
        )}
      </div>

      {/* No data state */}
      {!hasData && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-5 mb-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-700 mb-1">Complete your assessment first</p>
              <p className="text-xs text-amber-600 mb-3">
                Finance requests are built from your emissions data. Enter your data to get started.
              </p>
              <button onClick={() => navigate("/sme-journey")}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700">
                Start Journey <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active requests */}
      {requests.length === 0 ? (
        <div className="max-w-2xl">
          <SectionCard>

            <DollarSign size={32} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-500 mb-1">No financing requests yet</p>
            <p className="text-xs text-slate-400 mb-5">
              Submit a financing request after completing your climate assessment.
            </p>
            {hasData && (
              <button onClick={() => navigate("/sme-journey")}
                className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
                Go to Step 5 — Financing <ArrowRight size={14} />
              </button>
            )}
          
          </SectionCard>
        </div>
      ) : (
        <div className="max-w-3xl space-y-4">
          <p className="text-xs text-slate-400">{requests.length} request{requests.length !== 1 ? "s" : ""} found</p>
          {requests.map(req => (
            <RequestCard key={req.request_id} req={req} />
          ))}
        </div>
      )}

      {/* Info note */}
      <div className="mt-6 max-w-2xl flex items-start gap-2 text-xs text-slate-400">
        <Info size={13} className="flex-shrink-0 mt-0.5" />
        <p>
          Finance requests are reviewed by institutional partners in the DeCarbonIQ network.
          Your emissions data is frozen at submission time — updating your data will not affect existing requests.
        </p>
      </div>
    </div>
  );
}
